import { useCallback, useContext, useState } from 'react';

import resumeCardFavoriteButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/resume_card_favorite_button_click';
import { useBreakpoint } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { useNotification } from 'src/components/Notifications/Provider';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import putInFolder from 'src/components/ResumeFolders/putResumesFolder';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';

import Menu from 'src/components/ResumeCard/Actions/SaveToFavoritesButton/Menu';
import MenuModal from 'src/components/ResumeCard/Actions/SaveToFavoritesButton/Menu/Mobile';

const dataQAPrefix = 'resume-serp__favourite-popup';

const SaveToFavorites: TranslatedComponent<{ isMobile?: boolean; closeAdditionalMenu?: () => void }> = ({
    isMobile,
    closeAdditionalMenu,
}) => {
    const inEmployerSubstatesExp = useSelector((state) => state.isEmployerSubstatesExp);
    const isHiringManagerExp = useSelector((state) => state.isHiringManagerExperiment);
    const { isGtS, isM } = useBreakpoint();
    const { isResponse, isNew, vacancyId, lastEmployerStateExtName, resume, isChangeState } =
        useContext(ResumeCardActionsContext);
    const foldersIdList = resume.folders || [];
    const hash = resume._attributes.hash;
    const simHash = resume._attributes.sim_hash;
    const topicId = resume.topicId;
    const { addNotification } = useNotification();

    const employerManagerId = useSelector((state) => parseInt(state.employerManager?.id || '', 10));
    const [menuVisible, toggleMenuVisible, setMenuVisible] = useToggleState(false);
    const [isSaved, setIsSaved] = useState(foldersIdList?.length > 0);

    const onSuccess = (isSaved: boolean) => {
        setMenuVisible(false);
        setIsSaved(isSaved);
    };

    const onClick = useCallback(() => {
        toggleMenuVisible();
        if (topicId && lastEmployerStateExtName) {
            resumeCardFavoriteButtonClick({ vacancyId, topicId, folderName: lastEmployerStateExtName });
        }
    }, [lastEmployerStateExtName, toggleMenuVisible, topicId, vacancyId]);

    const onSave = putInFolder(
        {
            onSuccess,
            simHash,
            employerManagerId,
            resumeHash: hash,
        },
        addNotification
    );

    const folders = useSelector((state) => state.resumeFolders.foldersInvariants);

    if (
        isResponse &&
        !closeAdditionalMenu &&
        ((isM && (isHiringManagerExp || isNew)) ||
            (inEmployerSubstatesExp && ((isNew && isGtS) || isM) && isChangeState))
    ) {
        return null;
    }

    const Component = isMobile || closeAdditionalMenu ? MenuModal : Menu;

    return (
        <Component
            isVisible={menuVisible}
            setIsVisible={setMenuVisible}
            dataQAPrefix={dataQAPrefix}
            folders={folders}
            foldersIdList={foldersIdList}
            isSaved={isSaved}
            onClick={onClick}
            onSave={onSave}
            closeAdditionalMenu={closeAdditionalMenu}
            isMobile={isMobile}
        />
    );
};

export default translation(SaveToFavorites);
